import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// import DefaultSelect from "../More/DefaultSelect";
import ProductSection from "../../components/Product/ProductSection";
import CategoryTab from "../../components/CategoryTab/CategoryTab";
import instance from "../../services/services";
import { BRAND_EXCLUDE_CATEGORY_LIST_URL } from "../../utils/urls";
import useUserStore from "../../store/userStore";
import { useLanguage } from "../../hooks/useLanguage";
import PaginationSection from "../../components/Product/Pagination";

function CinemaPageSec() {
  const { t } = useLanguage();
  const pageRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const { member_seq } = useUserStore(state => state.userInfo);
  const [categoryInfo, setCategoryInfo] = useState([]);

  // 모바일 감지 상태 추가
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  // URL 파라미터 관리를 useMemo로 최적화
  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      title: searchParams.get('title') || 'Package',
      mainCategory: searchParams.get('mainCategory') || 'CINEMA',
      categoryCode: searchParams.get('category_code') || '',
      subCategoryCode: searchParams.get('sub') || '',
      activeSubCategory: searchParams.get('activeSub') || ''
    };
  }, [location.search]);

  const categoryData1 = urlParams.categoryCode.slice(0, 4);
  
  // 상태 단순화
  const [isLoading, setIsLoading] = useState(true);
  const [categoryData, setCategoryData] = useState({
    mainCategories: [],  // 상단 메인 카테고리 탭
    subCategories: [],   // 하위 카테고리 버튼
    products: [],         // 현재 선택된 카테고리의 상품
    allSubCategories: []  // 모든 하위 카테고리 데이터
  });

  // 각 카테고리별 페이지네이션 상태 관리
  const [categoryPages, setCategoryPages] = useState({});
  
  // itemsPerPage를 동적으로 계산하는 함수 추가
  const getItemsPerPage = useCallback(() => {
    return isMobile ? 4 : 10;
  }, [isMobile]);

  // 모바일 스크롤 페이지네이션을 위한 상태 추가
  const [isScrolling, setIsScrolling] = useState({});

  // 터치 시작 위치 저장용 ref
  const touchStartRef = useRef({});

  // 리사이즈 이벤트 핸들러 추가
  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      if (newIsMobile !== isMobile) {
        setIsMobile(newIsMobile);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  // 각 카테고리별 데이터를 가져오는 함수 수정
  const fetchCategoryData = useCallback(async (categoryId, page = 1, isPageClick = false) => {
    try {
      // 페이지 클릭일 때는 전체 로딩을 하지 않음
      if (!isPageClick) {
        setIsLoading(true);
      }
      
      const requestBody = {
        category_code: categoryId,
        member_seq: member_seq || '',
        searchSort: 'regist_date',
      };
      
      // 모바일일 때와 PC일 때 분기
      if (isMobile) {
        requestBody.mobileChk = "Y";
      } else {
        requestBody.offset = page;
        requestBody.limit = getItemsPerPage();
        
        if (isPageClick) {
          requestBody.type = 'detail';
        }
      }
      
      const response = await instance.post(BRAND_EXCLUDE_CATEGORY_LIST_URL, requestBody);

      const categoryData = response.data.data.category || {};
      
      // 해당 카테고리의 데이터만 업데이트
      if (categoryData[categoryId]) {
        setCategoryData(prev => ({
          ...prev,
          allSubCategories: prev.allSubCategories.map(category => 
            category.category_code === categoryId 
              ? { ...category, ...categoryData[categoryId] }
              : category
          )
        }));
      }
    } catch (error) {
      console.error('Error fetching category data:', error);
    } finally {
      // 페이지 클릭일 때는 전체 로딩을 하지 않음
      if (!isPageClick) {
        setIsLoading(false);
      }
    }
  }, [member_seq, getItemsPerPage, isMobile]);

  // goToPage 함수 추가
  const goToPage = useCallback((categoryId, page) => {
    // const currentCategory = categoryData.allSubCategories.find(cat => cat.category_code === categoryId);
    
    setIsScrolling(prev => ({ ...prev, [categoryId]: true }));
    setCategoryPages(prev => ({ ...prev, [categoryId]: page }));
    
    fetchCategoryData(categoryId, page, true).finally(() => {
      setTimeout(() => {
        setIsScrolling(prev => ({ ...prev, [categoryId]: false }));
      }, 200);
    });
  }, [fetchCategoryData]);

  // 터치 시작 핸들러
  const handleTouchStart = useCallback((e, categoryId) => {
    if (!isMobile) return;
    
    const touch = e.touches[0];
    touchStartRef.current[categoryId] = {
      x: touch.clientX,
      time: Date.now()
    };
  }, [isMobile]);

  // 터치 종료 핸들러 (드래그 방향 감지)
  const handleTouchEnd = useCallback((e, categoryId) => {
    if (!isMobile || isScrolling[categoryId]) return;
    
    const touchStart = touchStartRef.current[categoryId];
    if (!touchStart) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStart.x;
    const deltaTime = Date.now() - touchStart.time;
    
    // 최소 드래그 거리와 시간 체크
    if (Math.abs(deltaX) < 50 || deltaTime > 500) return;

    const currentCategory = categoryData.allSubCategories.find(cat => cat.category_code === categoryId);
    if (!currentCategory) return;

    const itemsPerPage = 4;
    const totalPages = Math.ceil(currentCategory.cntRow / itemsPerPage);
    const currentPage = categoryPages[categoryId] || 1;



    // 드래그 방향에 따른 페이지 이동
    if (deltaX > 0 && currentPage > 1) {
      // 오른쪽 드래그 = 이전 페이지
      goToPage(categoryId, currentPage - 1);
    } else if (deltaX < 0 && currentPage < totalPages) {
      // 왼쪽 드래그 = 다음 페이지
      goToPage(categoryId, currentPage + 1);
    }

    // 터치 데이터 정리
    delete touchStartRef.current[categoryId];
  }, [isMobile, isScrolling, categoryData.allSubCategories, categoryPages, goToPage]);

  // ProductSection에 스크롤 이벤트를 추가하는 useEffect 추가
  useEffect(() => {
    if (!isMobile || categoryData.allSubCategories.length === 0) return;
    
    const scrollHandlers = [];
    
    // DOM이 완전히 렌더링될 때까지 대기
    const timer = setTimeout(() => {
      categoryData.allSubCategories.forEach(category => {
        const productContainer = document.querySelector(
          `[data-category-id="${category.category_code}"] .products-grid`
        );
        
        if (productContainer) {
          // 마지막 페이지 아이템이 2개 미만일 때도 2열로 유지하도록 추가 스타일 적용
          const productItems = productContainer.querySelectorAll('.product-item');
          const totalItems = productItems.length;
          if (totalItems > 0) {
            // 마지막 페이지의 아이템 수 계산
            const itemsPerPage = 4;
            const currentPage = categoryPages[category.category_code] || 1;
            const itemsInCurrentPage = Math.min(itemsPerPage, totalItems - (currentPage - 1) * itemsPerPage);
            
            // 기존 플레이스홀더 제거
            const existingPlaceholders = productContainer.querySelectorAll('.grid-placeholder');
            existingPlaceholders.forEach(placeholder => placeholder.remove());
            
            // 마지막 페이지이고 아이템이 4개 미만일 때 플레이스홀더 추가
            if (currentPage === Math.ceil(category.cntRow / itemsPerPage) && itemsInCurrentPage < 4 && itemsInCurrentPage > 0) {
              const neededItems = 4 - itemsInCurrentPage;
              
              for (let i = 0; i < neededItems; i++) {
                const placeholder = document.createElement('div');
                placeholder.className = 'grid-placeholder';
                productContainer.appendChild(placeholder);
              }
            }
          }
          
          // 터치 이벤트 핸들러 추가
          const touchStartHandler = (e) => handleTouchStart(e, category.category_code);
          const touchEndHandler = (e) => handleTouchEnd(e, category.category_code);
          
          productContainer.addEventListener('touchstart', touchStartHandler, { passive: true });
          productContainer.addEventListener('touchend', touchEndHandler, { passive: true });
          
          scrollHandlers.push({ 
            container: productContainer, 
            touchStartHandler,
            touchEndHandler
          });
        }
      });
    }, 100);
    
    return () => {
      clearTimeout(timer);
      scrollHandlers.forEach(({ container, touchStartHandler, touchEndHandler }) => {
        if (touchStartHandler) container.removeEventListener('touchstart', touchStartHandler);
        if (touchEndHandler) container.removeEventListener('touchend', touchEndHandler);
      });
    };
  }, [categoryData.allSubCategories, isMobile, handleTouchStart, handleTouchEnd, categoryPages]);

  // 카테고리용 모바일 스와이퍼 옵션 추가 (2행 x 2열 = 4개)
  const getCategoryMobileSwiperOptions = (sectionId) => ({
    slidesPerView: 2,
    spaceBetween: 8,
    autoHeight: false,
    slidesPerGroup: 4,
    grid: {
      rows: 2,
      fill: 'row'
    },
    modules: [
      require('swiper/modules').Pagination, 
      require('swiper/modules').Grid,
      require('swiper/modules').Navigation
    ],
    pagination: { 
      clickable: true,
    },
    navigation: {
      nextEl: `#${sectionId}-next`,
      prevEl: `#${sectionId}-prev`,
    },
    loop: false,
    centeredSlides: false,
    allowTouchMove: true,
    breakpoints: {
      480: {
        slidesPerView: 2,
        spaceBetween: 8,
        slidesPerGroup: 4,
        grid: {
          rows: 2,
          fill: 'row'
        }
      }
    }
  });

  // 카테고리별 페이지 변경 핸들러 수정
  const handleCategoryPageChange = (categoryId, page) => {
    
    // 페이지 상태 업데이트
    setCategoryPages(prev => ({
      ...prev,
      [categoryId]: page
    }));
    
    // API 호출
    fetchCategoryData(categoryId, page, true);
    
    // 모바일에서 스크롤 위치 조정
    if (isMobile) {
      setTimeout(() => {
        const productContainer = document.querySelector(`[data-category-id="${categoryId}"] .products-grid`);
        if (productContainer) {
          const scrollPosition = (page - 1) * productContainer.clientWidth;
          productContainer.scrollTo({ left: scrollPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  };

  // 카테고리별 화살표 클릭 핸들러 수정
  const handleCategoryArrowClick = (categoryId, page) => {
    handleCategoryPageChange(categoryId, page);
  };
  
  useEffect(() => {
    const fetchCategoryData = async () => {
      setIsLoading(true);
      
      try {
        // 1. 메인 카테고리 데이터는 state에서 가져와서 바로 사용
        const mainCategories = location.state?.mainCategories || [];
        
        // 만약 state에 메인 카테고리 데이터가 없다면(페이지 새로고침 등의 경우)
        if (mainCategories.length === 0) {
          const mainRequestBody = {
            category_code: categoryData1,
            member_seq: member_seq || '',
            searchSort: 'regist_date'
          };
          
          // 모바일일 때와 PC일 때 분기
          if (isMobile) {
            mainRequestBody.mobileChk = "Y";
          } else {
            mainRequestBody.offset = 1;
          }
          
          const mainResponse = await instance.post(BRAND_EXCLUDE_CATEGORY_LIST_URL, mainRequestBody);

          const mainCategoryData = mainResponse.data.data.category || {};
          const newMainCategories = Object.keys(mainCategoryData)
            .filter(key => key !== "package_flag")
            .map(key => ({
              category_name: mainCategoryData[key].category_name,
              category_code: key
            }))
            .filter(category => category.category_name);
          
          setCategoryData(prev => ({
            ...prev,
            mainCategories: newMainCategories
          }));
        } else {
          setCategoryData(prev => ({
            ...prev,
            mainCategories
          }));
        }
        
        // 2. 하위 카테고리 데이터 가져오기
        if (location.state?.subCategories && location.state.subCategories.length > 0) {
          setCategoryData(prev => ({
            ...prev,
            subCategories: location.state.subCategories,
            products: location.state.selectedProducts || [],
            allSubCategories: location.state.allSubCategories || location.state.subCategories
          }));
        }
        
        // state에 하위 카테고리 데이터가 없으면 API 호출
        const codeToUse = urlParams.subCategoryCode || urlParams.categoryCode;
        
        if (codeToUse) {
          const subRequestBody = {
            category_code: codeToUse,
            member_seq: member_seq || '',
            searchSort: 'regist_date',
          };
          
          // 모바일일 때와 PC일 때 분기
          if (isMobile) {
            subRequestBody.mobileChk = "Y";
          } else {
            subRequestBody.offset = 1;
            subRequestBody.limit = getItemsPerPage();
          }
          
          const response = await instance.post(BRAND_EXCLUDE_CATEGORY_LIST_URL, subRequestBody);
          
          setCategoryInfo(response.data.data.categoryInfo);
          
          const subCategoryData = response.data.data.category || {};
          const subCategories = Object.keys(subCategoryData)
            .filter(key => key !== "package_flag")
            .map(key => ({
              category_name: subCategoryData[key].category_name,
              category_code: key,
              cntRow: subCategoryData[key].cntRow || "0",
              data: subCategoryData[key].data || [],
              package_flag: subCategoryData[key].package_flag || 'N'
            }))
            .filter(category => category.category_name);
          
          setCategoryData(prev => ({
            ...prev,
            subCategories,
            allSubCategories: subCategories
          }));
        }
      } catch (error) {
        console.error("카테고리 데이터 가져오기 오류:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCategoryData();
  }, [urlParams, member_seq, location.state, categoryData1, getItemsPerPage, isMobile]);
  
  // 하위 카테고리 클릭 핸들러 - URL 파라미터 업데이트
  const handleSubCategoryClick = (subCategory) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set('activeSub', subCategory.category_code);
    
    navigate(
      `/category/cinemaPageSecDetail?title=${subCategory.category_name}&sub=${subCategory.category_code}&activeSub=${subCategory.category_code}&mainCategory=${urlParams.mainCategory}&previousTitle=${urlParams.title}`,
      {
        state: { 
          mainCategory: urlParams.mainCategory,
          mainCategories: categoryData.mainCategories,
          previousTitle: urlParams.title,
          selectedCategoryCode: urlParams.categoryCode,
          subCategories: categoryData.subCategories,
          allSubCategories: categoryData.allSubCategories,
        }
      }
    );
  };
  
  // breadcrumb 클릭 핸들러 추가
  const handleBreadcrumbClick = (level) => {
    switch(level) {
      case 'home':
        navigate('/');
        break;
      case 'mainCategory':
        // 메인 카테고리 페이지로 이동 (이전 페이지)
        navigate(`/category/cinemaPage?title=${urlParams.mainCategory}&category_code=${categoryData1}`, {
          state: {
            mainCategories: categoryData.mainCategories
          }
        });
        break;
      default:
        break;
    }
  };

  return (
    <div ref={pageRef}>
      <section className="prod_section">
        <div 
          className="product_banner cate_brand"
          style={{backgroundImage:`url(${window.innerWidth > 768 ? categoryInfo?.pc_img_url : categoryInfo?.mobile_img_url})`}}
        >
          <div className="inner">
            <ul className="bread_crumb">
              <li className="group_item" style={{cursor: 'pointer'}} onClick={() => handleBreadcrumbClick('home')}>
                <p>HOME</p>
              </li>
              <li className="group_item" style={{cursor: 'pointer'}} onClick={() => handleBreadcrumbClick('mainCategory')}>
                <p>{urlParams.mainCategory}</p>
              </li>
              <li className="group_item active">
                <p>{urlParams.title}</p>
              </li>
            </ul>
            
            <div className="product_tit">
              <h4 className="tit">{urlParams.mainCategory}</h4>
              <p className="sub">{categoryInfo?.cate_comment}</p>
            </div>

            {/* 메인 카테고리 탭 - 항상 상단에 표시 */}
            {!isLoading && categoryData.mainCategories.length > 0 && (
              <CategoryTab 
                categoryNames={categoryData.mainCategories} 
                selectedCategoryCode={urlParams.categoryCode}
                mainCategory={urlParams.mainCategory}
              />
            )}
          </div>
        </div>

        <div className="inner main_prod_list cate_container gap_64">
          {isLoading ? (
            <div className="loading">.</div>
          ) : (
            <div>
              <div className="brand_top_tab">
                <div className="brand_section_title">
                  {urlParams.title}
                </div>

                {/* 하위 카테고리 버튼 그룹 */}
                <div className="btn_group flex-justify-center flex-wrap">
                  {categoryData.subCategories.map((subCategory, index) => (
                    <button 
                      key={index} 
                      className={`tap_btn ${urlParams.activeSubCategory === subCategory.category_code ? 'active' : ''}`} 
                      type="button"
                      onClick={() => handleSubCategoryClick(subCategory)}
                    >
                      {subCategory.category_name}
                    </button>
                  ))}
                </div>
              </div>

              {/* 모든 하위 카테고리를 반복하여 표시 */}
              {categoryData.allSubCategories && categoryData.allSubCategories.map((subCategory, index) => (
                // 데이터가 있는 카테고리만 표시
                subCategory.data && subCategory.data.length > 0 && (
                  <div key={index} className="category-section">
                    <div className="brand_top_tab">
                    {subCategory.data.length > 0 && subCategory.category_name !== "Package" && (
                      <div className="brand_section_title_sub" style={{marginTop:index === 0 ? "32px" : "64px"}}>
                        {subCategory.category_name}
                      </div>
                      )}
                    </div>

                    <div className="p0 cate_cinema_con" data-category-id={subCategory.category_code}>
                      {/* beforeContent 영역 - ProductSection 밖에 배치 */}
                      <div className="auth_find" style={{marginTop:"16px", marginBottom:"-16px", padding:0}}>
                        <div className="product_total" style={{paddingLeft:"16px"}}>
                          {t('product_total_count', {count: subCategory.cntRow})}
                        </div>
                        <div 
                          className="more_btn" 
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(
                            `/category/cinemaPageSecDetail?title=${subCategory.category_name}&sub=${subCategory.category_code}&activeSub=${subCategory.category_code}&mainCategory=${urlParams.mainCategory}&previousTitle=${urlParams.title}`,
                            {
                              state: { 
                                mainCategory: urlParams.mainCategory,
                                mainCategories: categoryData.mainCategories,
                                previousTitle: urlParams.title,
                                selectedCategoryCode: urlParams.categoryCode
                              }
                            }
                          )}
                        >
                          {t('product_more_view')}
                        </div>
                      </div>
                      
                      {/* 모바일: 스와이퍼, PC: 일반 그리드 */}
                      {isMobile ? (
                        <div className="swiper_parent" style={{ position: 'relative' }}>
                          <ProductSection
                            products={subCategory.data}
                            showTabs={false}
                            showMoreButton={false}
                            useSwiper={true}
                            swiperOptions={getCategoryMobileSwiperOptions(`category-${subCategory.category_code}`)}
                            sectionId={`category-${subCategory.category_code}`}
                            packageFlag={subCategory.package_flag}
                          />
                          
                          {/* 네비게이션 버튼 */}
                          <div 
                            id={`category-${subCategory.category_code}-prev`}
                            className="swiper-button-prev"
                            style={{
                              position: 'absolute',
                              top: '47%',
                              left: '10px',
                              transform: 'translateY(-50%)',
                              width: '40px',
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              zIndex: 10,
                            }}
                          />
                          <div 
                            id={`category-${subCategory.category_code}-next`}
                            className="swiper-button-next"
                            style={{
                              position: 'absolute',
                              top: '47%',
                              right: '10px',
                              transform: 'translateY(-50%)',
                              width: '40px',
                              height: '40px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              zIndex: 10,
                            }}
                          />
                        </div>
                      ) : (
                        <>
                          <ProductSection
                            products={subCategory.data}
                            showTabs={false}
                            showMoreButton={false}
                            useSwiper={false}
                            packageFlag={subCategory.package_flag}
                          />

                          {/* PC에서만 페이지네이션 표시 */}
                          <div className="pagination_wrapper" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
                            <PaginationSection
                              totalCount={parseInt(subCategory.cntRow)}
                              itemsPerPage={getItemsPerPage()}
                              currentPage={categoryPages[subCategory.category_code] || 1}
                              onPageChange={(page) => handleCategoryPageChange(subCategory.category_code, page)}
                              onArrow={(page) => handleCategoryArrowClick(subCategory.category_code, page)}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default CinemaPageSec;